/* FishLog Card */
import { useNavigate } from "react-router-dom";
import { useLang } from "../contexts/LangContext";
import { FishConsumptionUnit } from "../types/data";
import { convertNumberInCurrentLang } from "../utils/misc";
import NutrientMeter from "./NutrientMeter";
import { fishDatabase } from "../data/fish_db";
import { useState, useEffect } from "react";
import { useLocation } from "react-router-dom";
import { storageService } from "../services/storage.service";

const FishLog = ({ item, compact = false, showNutrients = true }: { item: FishConsumptionUnit; compact?: boolean; showNutrients?: boolean }) => {
  const navigate = useNavigate();
  const { t, lang } = useLang();
  const location = useLocation();
  const [showNutrientOutcome, setShowNutrientOutcome] = useState(true);
  const [showNutrientDRI, setShowNutrientDRI] = useState(false);

  useEffect(() => {
    storageService.getSettings().then(settings => {
      setShowNutrientOutcome(settings.showNutrientOutcome ?? true);
      setShowNutrientDRI(settings.showNutrientDRI ?? false);
    }).catch(console.error);
  }, []);

  // Determine if nutrients should be shown.
  // "Other" fish (fish_id "other") is not in the fish database and has no nutrients.
  const shouldShowNutrients =
    showNutrients && showNutrientOutcome && fishDatabase.some((f) => f.id === item.fish_id);

  const consumedParts = [
    item.consumed_soft_bone && t("fishItem_SoftBone"),
  ]
    .filter(Boolean)
    .join(", ");

  const formatExtraInfo = (metadata: any) => {
    // format metadata.meal_location, metadata.fish_source, etc. into a readable string
    const infoParts = [];
    if (metadata.meal_location) {   
      const icon = metadata.meal_location === 'home' ? '🏠' :
                    metadata.meal_location === 'restaurant' ? '🍽️' :
                    metadata.meal_location === 'market_stall' ? '🏪' :
                    metadata.meal_location === 'outdoor' ? '🌳' :
                    metadata.meal_location === 'other' ? '📍' : '';
     
      infoParts.push(`${icon} ${t("extraInfo_Display_ConsumedAt")}: ${t(`extraInfo_Location_${metadata.meal_location.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}` as any)}`);
    }

    if (metadata.fish_source) {
      const icon = metadata.fish_source === 'market' ? '🛒' :
                    metadata.fish_source === 'self_caught' ? '🎣' :
                    metadata.fish_source === 'farmed' ? '🏞️' :
                    metadata.fish_source === 'gift' ? '🎁' : '';
      infoParts.push(`${icon} ${t("extraInfo_Display_Source")}: ${t(`extraInfo_Source_${metadata.fish_source.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}` as any)}`);
    }

    if (metadata.prepared_by) {
      const icon = metadata.prepared_by === 'self' ? '👤' :
                    metadata.prepared_by === 'family' ? '👨‍👩‍👧' :
                    metadata.prepared_by === 'restaurant' ? '👨‍🍳' :
                    metadata.prepared_by === 'other' ? '👥' : '';
      infoParts.push(`${icon} ${t("extraInfo_Display_PreparedBy")}: ${t(`extraInfo_PreparedBy_${metadata.prepared_by.charAt(0).toUpperCase() + metadata.prepared_by.slice(1)}` as any)}`);
    }
    if (metadata.co_ingredients) {
      const icon = metadata.co_ingredients === 'potato' ? '🥔' :
                    metadata.co_ingredients === 'other_vegetables' ? '🥬' :
                    metadata.co_ingredients === 'no_vegetable' ? '🚫' : '';
      infoParts.push(`${icon} ${t("extraInfo_Display_CoIngredients")}: ${t(`extraInfo_CoIngredients_${metadata.co_ingredients.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}` as any)}`);
    }
    if (metadata.people_sharing && metadata.people_sharing > 1) {
      infoParts.push(`👥 ${t("extraInfo_Display_SharedBy")}: ${metadata.people_sharing} ${t("extraInfo_PeopleSharing_Short")}`);
    }
    
    return infoParts.join(" • ");
  };

  return (
    <table className={`w-full fish-log-table ${compact ? 'text-sm' : 'mt-2'}`}>
      <colgroup>
        <col style={{ width: "50%" }} />
        <col style={{ width: "50%" }} />
      </colgroup>
      <tbody>
        {!compact && (
          <tr>
            <td className="font-semibold text-left">{t("fishItem_Name")}</td>
            <td>{item.fish_name}</td>
          </tr>
        )}
        {item.recipe && (
          <tr>
            <td className="font-semibold text-left">{t("fishItem_Recipe")}</td>
            <td>{t(`whichRecipe_${item.recipe}` as any)}</td>
          </tr>
        )}
        {item.portion_type && item.portion_type !== "unknown" && (
          <tr>
            <td className="font-semibold text-left">{t("fishItem_PortionType")}</td>
            <td>{t(`portionType_${item.portion_type}` as any)}</td>
          </tr>
        )}
        {item.piece_type && (
          <tr>
            <td className="font-semibold text-left">{t("fishItem_PieceType")}</td>
            <td>{t(`pieceType_${item.piece_type}` as any)}</td>
          </tr>
        )}
        {item.portion_type && item.portion_type !== "unknown" && (
          <tr>
            <td className="font-semibold text-left">
              {t(`fishItem_NumberOfPortions_${item.portion_type}` as any)}
            </td>
            <td>
              {convertNumberInCurrentLang(lang, item.number_of_portions || 0)}
            </td>
          </tr>
        )}
        {item.portion_type &&
          item.portion_type !== "unknown" &&
          (item.number_of_portions || 1) > 1 && (
            <tr>
              <td className="font-semibold text-left">
                {t(`fishItem_PortionWeight_${item.portion_type}` as any)}
              </td>
              <td>
                {convertNumberInCurrentLang(lang, item.weight_per_portion || 0)}{" "}
                {t(item.weight_unit === "oz" ? "unit_oz" : "unit_gm")}
              </td>
            </tr>
          )}
        <tr>
          <td className="font-semibold text-left">{t("fishItem_TotalQuantity")}</td>
          <td>
            {convertNumberInCurrentLang(lang, item.estimated_weight || 0)}{" "}
            {t(item.weight_unit === "oz" ? "unit_oz" : "unit_gm")}
          </td>
        </tr>
        {}
        {consumedParts && (
          <tr>
            <td className="font-semibold text-left">{t("fishItem_ConsumedParts")}</td>
            <td>{consumedParts}</td>
          </tr>
        )}
        {(() => {
          // Try new extra_info field first, fall back to legacy META in ingredients
          const metadata = item.extra_info ?? (() => {
            const ingredientsText = item.ingredients || '';
            const metaMatch = ingredientsText.match(/\[META: (.+?)\]/);
            return metaMatch ? JSON.parse(metaMatch[1]) : null;
          })();
          // Clean ingredients for display (strip legacy META if present)
          const cleanIngredients = (item.ingredients || '').replace(/\[META: .+?\]/g, '').trim();
          
          if (metadata || cleanIngredients) {
            return (
              <tr>
                <td className="font-semibold text-left align-top">{t("fishItem_ExtraInfo")}</td>
                <td className="text-sm space-y-1">
                  {metadata && (
                    <div className="text-gray-600 text-xs">
                      {formatExtraInfo(metadata)}
                    </div>
                    // <div className="">
                    //   {metadata.meal_location && (
                    //     <span className="text-xs">
                    //       <span>
                    //       {metadata.meal_location === 'home' && '🏠'}
                    //       {metadata.meal_location === 'restaurant' && '🍽️'}
                    //       {metadata.meal_location === 'market_stall' && '🏪'}
                    //       {metadata.meal_location === 'outdoor' && '🌳'}
                    //       {metadata.meal_location === 'other' && '📍'}
                    //       </span>
                    //       <span>{t("extraInfo_Display_ConsumedAt")}: {t(`extraInfo_Location_${metadata.meal_location.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}` as any)}</span>
                    //     </span>
                    //   )}
                    //   •
                    //   {metadata.fish_source && (
                    //     <span className="text-xs">
                    //       {metadata.fish_source === 'market' && '🛒'}
                    //       {metadata.fish_source === 'self_caught' && '🎣'}
                    //       {metadata.fish_source === 'farmed' && '🏞️'}
                    //       {metadata.fish_source === 'gift' && '🎁'}
                    //       <span>{t("extraInfo_Display_Source")}: {t(`extraInfo_Source_${metadata.fish_source.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}` as any)}</span>
                    //     </span>
                    //   )}
                    //   {metadata.prepared_by && (
                    //     <span className="text-xs">
                    //       {metadata.prepared_by === 'self' && '👤'}
                    //       {metadata.prepared_by === 'family' && '👨‍👩‍👧'}
                    //       {metadata.prepared_by === 'restaurant' && '👨‍🍳'}
                    //       {metadata.prepared_by === 'other' && '👥'}
                    //       <span>{t("extraInfo_Display_PreparedBy")}: {t(`extraInfo_PreparedBy_${metadata.prepared_by.charAt(0).toUpperCase() + metadata.prepared_by.slice(1)}` as any)}</span>
                    //     </span>
                    //   )}
                    //   {metadata.co_ingredients && (
                    //     <span className="text-xs">
                    //       {metadata.co_ingredients === 'potato' && '🥔'}
                    //       {metadata.co_ingredients === 'other_vegetables' && '🥬'}
                    //       {metadata.co_ingredients === 'no_vegetable' && '🚫'}
                    //       <span>{t("extraInfo_Display_CoIngredients")}: {t(`extraInfo_CoIngredients_${metadata.co_ingredients.split('_').map((word: string) => word.charAt(0).toUpperCase() + word.slice(1)).join('')}` as any)}</span>
                    //     </span>
                    //   )}
                    //   {metadata.people_sharing && metadata.people_sharing > 1 && (
                    //     <span className="text-xs">
                    //       👥 {t("extraInfo_SharedBy")}: {metadata.people_sharing} {t("extraInfo_PeopleSharing_Short")}
                    //     </span>
                    //   )}
                    // </div>
                  )}
                  {cleanIngredients && (
                    <div className="text-gray-700 text-xs mt-1">
                      <span className="font-semibold">{t("fishItem_IngredientsLabel")}:</span> {cleanIngredients}
                    </div>
                  )}
                </td>
              </tr>
            );
          }
          return null;
        })()}
        {shouldShowNutrients && (
          <tr>
            <td colSpan={2}>
              <NutrientMeter
                fishes={[
                  {
                    fishId: item.fish_id,
                    amount_in_gm: item.estimated_weight || 0,
                  },
                ]}
                showDRI={showNutrientDRI}
              />
            </td>
          </tr>
        )}
      </tbody>
    </table>
  );
};

export default FishLog;
